import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { TrailPersonal } from '../../dto/trail-personal';
import { Subject, takeUntil } from 'rxjs';
import { TrailPersonalService } from '../../service/trail-personal.service';
import { Trail } from '../../dto/trail';
import { TrailPersonalDetailsComponent } from '../trail-personal-details/trail-personal-details.component';

@Component({
  selector: 'dpi-trail-personal',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule, MatTooltipModule, NgIf],
  templateUrl: './trail-personal.component.html',
  styleUrl: './trail-personal.component.scss'
})
export class TrailPersonalComponent implements OnInit, OnDestroy {
  trail: Trail;
  displayedColumns: string[] = ['name', 'position', 'company', 'certificate', 'remarks'];
  dataSource: TrailPersonal[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailPersonalService: TrailPersonalService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailPersonalService.getAllPersonalOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectTrailPersonal(selected: TrailPersonal) {
    if (!this.isReadonly) {
      var objCopy: TrailPersonal = { ...selected };
      const dialogRef = this._dialog.open(TrailPersonalDetailsComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.updatePersonal(dialogRef.componentInstance.trailPersonal);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.deletePersonal(dialogRef.componentInstance.trailPersonal.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  createPersonal() {
    this.trailPersonalService.createPersonal(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailPersonal(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailPersonal) {
    let list: TrailPersonal[] = [];
    for(var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  updatePersonal(personal: TrailPersonal) {
    this.trailPersonalService.updatePersonal(personal)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.replaceModelInDataSource(model);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailPersonal) {
    let list: TrailPersonal[] = [];
    for(var item of this.dataSource) {
      if (item.id === model.id) {
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  deletePersonal(personalId: number) {
    this.trailPersonalService.deletePersonal(personalId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(personalId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(personalId: number) {
    let list: TrailPersonal[] = [];
    for(var item of this.dataSource) {
      if (item.id === personalId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

}
